import {
    useMemo,
    useCallback,
} from 'react'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import DeleteIcon from '@mui/icons-material/Delete'

import InputBase from 'app/shared-components/InputBase'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    FeeChargeability,
    ProductType,
} from 'app/enums/label.enums'

import {
    FeesType,
} from 'app/enums/common.enums'
import InputIntegerNumbers from 'app/shared-components/InputIntegerNumbers'

import useFeesNameToFeesChargeabilityMapping from '../useFeesNameToFeesChargeabilityMapping'
import {
    feeNames, feeChargeabilityWithoutValues, CHARGEABILITIES_WITH_PRODUCT_TYPE,
} from '../Fee.utils'
import useFeePricingName from './useFeePricingName'
import Select from './Select'
import Percentage from './Percentage'

import useStyles from './FeeInputs.styles'

type Props = {
    value?: {
        id?: string | number,
        feeChargeability?: string,
        productType?: string,
        feeName?: string,
        rangeType?: string,
        feeDescription?: string,
        feeValue?: number,
    },
    onChange: (tempValue: any) => void,
    onDelete: (id: string | number) => void,
    disabled?: boolean,
    productTypes?: {
        productType: string,
        temperatureRange: string,
    }[],
    tempRange?: string,
}

const defaultProps: Partial<Props> = {
    value: {},
    disabled: false,
    productTypes: [],
    tempRange: undefined,
}

const FeeInputs = ({
    value,
    onChange,
    disabled,
    onDelete,
    productTypes,
    tempRange,
}: Props) => {
    const {
        classes,
    } = useStyles()
    const getEnumValues = useEnumValues()
    const allowedProductTypes = getEnumValues(ProductType)
    const allowedFeeChargeabilities = getEnumValues(FeeChargeability)
    const feePricingName = useFeePricingName()
    const name = feePricingName || FeesType.Lane
    const chargeabilityMapping = useFeesNameToFeesChargeabilityMapping(name)

    const feeNameOptions = useMemo(() => {
        return Object.entries(
            getEnumValues(feeNames[name]),
        )
    },
    [
        name,
        getEnumValues,
    ])

    const productTypesOptions = useMemo(() => {
        const list = tempRange ? Object.values(productTypes)?.filter((pt) => {
            return pt.temperatureRange === tempRange
        }) : Object.values(productTypes)

        return list?.reduce((acc, element) => {
            return {
                ...acc,
                [element.productType]: allowedProductTypes[element.productType],
            }
        }, {})
    }, [
        productTypes,
        allowedProductTypes,
        tempRange,
    ])

    const chargeabilityOptions = useMemo(() => {
        if (chargeabilityMapping) {
            const optionKeys = chargeabilityMapping[value.feeName] as string[]

            return optionKeys ? optionKeys.map((key) => {
                return [
                    key,
                    allowedFeeChargeabilities[key],
                ]
            }) : []
        }
        return []
    },
    [
        allowedFeeChargeabilities,
        chargeabilityMapping,
        value.feeName,
    ])

    const showProductTypeSelect = useMemo(() => {
        return CHARGEABILITIES_WITH_PRODUCT_TYPE.includes(value.feeChargeability)
    }, [value.feeChargeability])

    const deleteFee = useCallback(() => {
        onDelete(value.id)
    }, [
        value.id,
        onDelete,
    ])

    const handleChange = useCallback((change, {
        target: {
            name: changeName,
        },
    }) => {
        const feeNameChanged = changeName === 'feeName'
        const feeChargeabilityChanged = changeName === 'feeChargeability'

        onChange({
            ...value,
            feeChargeability: feeNameChanged ? null
                : value?.feeChargeability,
            productType: (feeNameChanged || feeChargeabilityChanged)
                ? null : value?.productType,
            feeValue: (feeNameChanged || feeChargeabilityChanged || changeName === 'productType')
                ? null : value?.feeValue,
            [changeName]: change,
        })
    }, [
        onChange,
        value,
    ])

    return (
        <TableRow
            key={`fee-item_${value.id}`}
            data-testid={`feeInput-${value.feeName}`}
        >
            <TableCell
                component="th"
                scope="fee"
                classes={{
                    root: classes.fee,
                }}
            >
                <Select
                    data={feeNameOptions}
                    value={value.feeName}
                    name="feeName"
                    onChange={handleChange}
                    disableUnderline
                    disableHelperText
                    disabled={disabled}
                    required={!disabled}
                />
            </TableCell>
            <TableCell
                classes={{
                    root: classes.feeChargeability,
                }}
            >
                <Select
                    data={chargeabilityOptions}
                    value={value.feeChargeability}
                    name="feeChargeability"
                    onChange={handleChange}
                    disableUnderline
                    disableHelperText
                    disabled={disabled}
                    required={!disabled}
                />
            </TableCell>
            <TableCell
                classes={{
                    root: classes.productType,
                }}
            >
                {showProductTypeSelect && (
                    <Select
                        allowedValues={productTypesOptions}
                        value={value.productType}
                        name="productType"
                        onChange={handleChange}
                        disableUnderline
                        disableHelperText
                        disabled={disabled}
                        required={!disabled}
                    />
                )}
            </TableCell>
            <TableCell
                align="right"
                classes={{
                    root: classes.descriptionCell,
                }}
            >
                <InputBase
                    value={value.feeDescription}
                    name="feeDescription"
                    onChange={handleChange}
                    disabled={disabled}
                    classes={{
                        root: classes.description,
                    }}
                    inputProps={{
                        className: !disabled && classes.input,
                    }}
                />
            </TableCell>
            {!value.feeChargeability || feeChargeabilityWithoutValues.some((feeChargability) => {
                return feeChargability === value.feeChargeability
            })
                ? (
                    <TableCell
                        classes={{
                            root: classes.tableCell,
                        }}
                    />
                )
                : (
                    <>
                        {value.feeChargeability === 'PERCENTAGE_BASE_LEASE'
                            ? (
                                <Percentage
                                    value={value}
                                    onChange={handleChange}
                                    disabled={disabled}
                                />
                            )
                            : (
                                <TableCell
                                    classes={{
                                        root: classes.amount,
                                    }}
                                    data-testid="feeValue"
                                >
                                    <div className={classes.fieldWrapper}>
                                        <InputIntegerNumbers
                                            value={value?.feeValue ?? ''}
                                            inputProps={{
                                                className: !disabled && classes.input,
                                            }}
                                            name="feeValue"
                                            disabled={disabled}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </TableCell>
                            )}
                    </>
                )}
            {!disabled
                && (
                    <TableCell classes={{
                        root: classes.delete,
                    }}
                    >
                        <DeleteIcon
                            className={classes.deleteIcon}
                            onClick={deleteFee}
                        >
                            Delete
                        </DeleteIcon>
                    </TableCell>
                )}
        </TableRow>
    )
}

FeeInputs.defaultProps = defaultProps

export default FeeInputs
